import React, { useEffect, useMemo, useState } from 'react';
import {
    DataGridPro,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarFilterButton,
    GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';
import Typography from '@mui/material/Typography';
import { Box, Button, Chip, Paper, Stack } from '@mui/material';
import ConfirmDeleteDialogHazard from './Modal/ConfirmDeleteHazard';
import EditMenuButton from './Modal/EditMenuButton';
import AuditChecklistApiService from '../api/AuditChecklistApiService';
import EditAuditChecklistModal from './Modal/Admin/EditAuditChecklistModal';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from './Common/SnackbarProvider';

const auditChecklistApiService = new AuditChecklistApiService();

const AuditChecklistAdmin = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();
    const [auditChecklists, setAuditChecklists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(100);
    const [currentSelectedAuditChecklist, setCurrentSelectedAuditChecklist] = useState();
    const [openEditAuditChecklistModal, setOpenEditAuditChecklistModal] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ p: 1, justifyContent: 'space-between' }}>
                <Stack direction="row" spacing={1}>
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                </Stack>
                <GridToolbarQuickFilter debounceMs={300} />
            </GridToolbarContainer>
        );
    }

    const columns = useMemo(() => [
        {
            field: 'acId',
            headerName: t('common.id'),
            flex: 0.2,
        },
        {
            field: 'acTitle',
            headerName: t('common.title'),
            flex: 1.2,
        },
        {
            field: 'organizationName',
            headerName: t('fieldName.organization'),
            flex: 0.8,
        },
        {
            field: 'projectName',
            headerName: t('fieldName.location'),
            flex: 0.8,
        },
        {
            field: 'auditChecklistItems',
            headerName: t('auditChecklistAdminPage.column.items'),
            flex: 0.4,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const count = params.value?.length || 0;
                return (
                    <Chip
                        label={count}
                        size="small"
                        variant="outlined"
                    />
                );
            },
        },
        {
            field: 'acIsActive',
            headerName: t('fieldName.status'),
            flex: 0.4,
            renderCell: (params) => (
                <Chip
                    label={params.row.acIsActive ? t('common.active') : t('common.inactive')}
                    size="small"
                    color={params.row.acIsActive ? 'success' : 'default'}
                    variant={params.row.acIsActive ? 'filled' : 'outlined'}
                />
            ),
        },
        {
            field: 'edit',
            headerName: '',
            flex: 0.2,
            filterable: false,
            sortable: false,
            renderCell: (params) => {
                return <EditMenuButton
                    params={params}
                    setShowDeleteDialog={setShowDeleteDialog}
                    setCurrentSelectedRecord={setCurrentSelectedAuditChecklist}
                    setShowEditDialog={setOpenEditAuditChecklistModal}
                    onEditFunc={() => {
                        setOpenEditAuditChecklistModal(true);
                        setCurrentSelectedAuditChecklist(params.row);
                    }}
                />;
            },
        },
    ], [t]);

    useEffect(() => {
        async function setupAuditChecklists () {
            await populateAuditChecklists();
        }

        void setupAuditChecklists();
    }, []);

    async function populateAuditChecklists () {
        setLoading(true);
        let auditChecklistResponse = await auditChecklistApiService.getAuditChecklists();
        setAuditChecklists(auditChecklistResponse.data);
        setLoading(false);
    }

    const dialogTitle = () => {
        return t('auditChecklistAdminPage.confirmDeleteTitle', { name: currentSelectedAuditChecklist?.acTitle ?? '' });
    };

    const dialogLabel = () => {
        return t('auditChecklistAdminPage.confirmDeleteLabel', { name: currentSelectedAuditChecklist?.acTitle ?? '' });
    };

    async function onDeleteLocationClick (deleteValue) {
        if (deleteValue) {
            let deleteAuditChecklistResponse = await auditChecklistApiService.deleteAuditChecklist(currentSelectedAuditChecklist);

            if (deleteAuditChecklistResponse.success === true)
            {
                snackbar.success(t('auditChecklistAdminPage.toast.deleted', { name: currentSelectedAuditChecklist.acTitle }));

                await populateAuditChecklists();
            }

            setShowDeleteDialog(false);
            setCurrentSelectedAuditChecklist(null);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--ct-header-h, 56px))' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4">
                    {t('auditChecklistAdminPage.title')}
                </Typography>
                <Button
                    size="large"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenEditAuditChecklistModal(true)}
                >
                    {t('auditChecklistAdminPage.createButton')}
                </Button>
            </Stack>
            <Paper elevation={1} sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                <DataGridPro
                    rows={auditChecklists}
                    columns={columns}
                    loading={loading}
                    pagination
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    getRowId={(row) => row.acId}
                    components={{
                        Toolbar: CustomToolbar,
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100, 200]}
                    disableSelectionOnClick
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#fafafa',
                            borderBottom: '2px solid #e0e0e0',
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: '#f5f5f5',
                        },
                    }}
                />
            </Paper>
            <EditAuditChecklistModal
                open={openEditAuditChecklistModal}
                currentAuditChecklist={currentSelectedAuditChecklist}
                onCancel={() => {
                    setOpenEditAuditChecklistModal(false);
                    setCurrentSelectedAuditChecklist(null);
                }}
                onSave={async () => {
                    await populateAuditChecklists();
                    setOpenEditAuditChecklistModal(false);
                    setCurrentSelectedAuditChecklist(null);
                }}
            />
            <ConfirmDeleteDialogHazard
                keepMounted
                value={currentSelectedAuditChecklist}
                open={showDeleteDialog}
                onClose={onDeleteLocationClick}
                deleteTitle={dialogTitle()}
                deleteLabel={dialogLabel()}
            ></ConfirmDeleteDialogHazard>
        </Box>
    );
};
export default AuditChecklistAdmin;
